<template>
<div class="pagination">
  <span class="pagination__button"
        :class="{'pagination__button_disabled': isPrevDisabled}"
        @click="setPrev()">Prev</span>

  <template v-for="(item) in paginationItems">
    <span v-if="item !== '...'"
          class="pagination__button"
          :class="{'pagination__button_active': item === currentPage + 1}"
          @click="setPage(item)">{{item}}</span>
    <span v-else
          class="pagination__splitter">...</span>
  </template>
  <span class="pagination__button"
        :class="{'pagination__button_disabled': isNextDisabled}"
        @click="setNext()">Next</span>
</div>
</template>

<script>
/**
   * is used for table pagination
   * emit pagination status like { from: start_item_number, to: end_item_number }
   */
export default {
  name: 'BasePagination',
  data () {
    return {
      nextItem: {
        type: 'button',
        label: 'Next'
      },
      prevItem: {
        type: 'button',
        label: 'Prev'
      },
      currentPage: 0
    }
  },
  props: {
    totalItems: {
      type: Number,
      required: true
    },
    limit: {
      type: Number,
      default: 50
    }
  },
  computed: {
    pagesCount () {
      return Math.ceil(this.totalItems / this.limit)
    },
    paginationItems () {
      if (this.pagesCount <= 7) {
        return Array.from(Array(this.pagesCount), (x, i) => i + 1)
      }

      if (this.currentPage <= 2) {
        const head = Array.from(Array(5), (x, i) => i + 1)
        return [...head, '...', this.pagesCount]
      }

      if (this.currentPage === 3) {
        const head = Array.from(Array(6), (x, i) => i + 1)
        return [...head, '...', this.pagesCount]
      }

      if (this.currentPage >= this.pagesCount - 3) {
        const modifier = this.pagesCount - 4
        const tail = Array.from(Array(5), (x, i) => i + modifier)
        return [1, '...', ...tail]
      }

      if (this.currentPage === this.pagesCount - 4) {
        const modifier = this.pagesCount - 4
        const tail = Array.from(Array(6), (x, i) => i + modifier)
        return [1, '...', ...tail]
      }

      const modifier = this.currentPage - 1
      const body = Array.from(Array(5), (x, i) => i + modifier)

      return [1, '...', ...body, '...', this.pagesCount]
    },
    isPrevDisabled () {
      return this.currentPage <= 0
    },
    isNextDisabled () {
      return this.currentPage >= this.pagesCount - 1
    }
  },
  methods: {
    setPrev () {
      if (this.isPrevDisabled) {
        return
      }

      --this.currentPage
    },
    setNext () {
      if (this.isNextDisabled) {
        return
      }

      ++this.currentPage
    },
    setPage (number) {
      if (number === this.currentPage + 1) {
        return
      }

      this.currentPage = number - 1
    }
  },
  watch: {
    totalItems () {
      this.currentPage = 0
    },
    currentPage () {
      const from = this.currentPage * this.limit
      const to = (from + this.limit) < this.totalItems ? from + this.limit : this.totalItems
      this.$emit('pageChanged', { from, to })
    }
  }
}
</script>

<style scoped lang="scss">
.pagination {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  user-select: none;

  &__button {
    display: block;
    height: 30px;
    line-height: 30px;
    min-width: 30px;
    padding: 0 5px;
    text-align: center;
    margin: 0 5px;
    border-radius: 15px;
    color: $rfx-light-march;
    cursor: pointer;
    font-size: 13px;
    box-sizing: border-box;
    font-weight: 500;

    &:first-child {
      margin-right: 50px;
      color: $rfx-dark-march
    }

    &:last-child {
      margin-left: 50px;
      color: $rfx-dark-march
    }

    &:hover {
      background-color: $rfx-almost-white;
    }

    &_active {
      color: $rfx-bright-blue;
      background-color: $rfx-almost-white;
      cursor: default;
    }

    &_disabled {
      color: $rfx-light-grey !important;
      background-color: transparent !important;
      cursor: default;
    }

  }
}
</style>
